// Phase 0084 — Role Detail editor.
// Tabbed UI: Responsibilities | KPIs | Authorities | Records | Skills |
//            Training | Compliance | Reporting | Backup | Version History.
//
// Detailed content is empty when first opened — HR fills it in over time
// without any code change. Every save creates a version row.

import { useState } from 'react'
import {
  App, Alert, Badge, Button, Card, Col, Descriptions, Drawer, Empty, Form,
  Input, InputNumber, Modal, Row, Select, Space, Switch, Table, Tag, Tabs,
  Typography,
} from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { CheckOutlined, CloseOutlined, SendOutlined } from '@ant-design/icons'

import {
  roleMasterApi, type Kind, type RoleBundle, type VersionRow,
} from '@/features/hr/roleMasterApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text, Paragraph } = Typography

const RESPONSIBILITY_TYPES = [
  'DAILY', 'WEEKLY', 'MONTHLY', 'SHIFT_START', 'SHIFT_END',
  'SAFETY', 'QUALITY', 'PRODUCTION', 'MAINTENANCE', 'COMPLIANCE',
  'REPORTING', 'TRAINING', 'HOUSEKEEPING', 'ESCALATION', 'APPROVAL',
  'RECORD_MAINTENANCE',
]
const FREQUENCIES = [
  'EVERY_SHIFT', 'DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY',
  'YEARLY', 'AS_REQUIRED', 'EVENT_BASED',
]
const PRIORITIES = ['P1', 'P2', 'P3']

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  PENDING_APPROVAL: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  OBSOLETE: 'default',
  DETAILS_PENDING: 'blue',
}

export default function RoleDetailPage() {
  const { roleCode } = useParams<{ roleCode: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message: msg, modal } = App.useApp()

  const { data: bundle, isLoading, refetch } = useQuery({
    queryKey: ['role-detail', roleCode],
    queryFn: () => roleMasterApi.getRole(roleCode!),
    enabled: !!roleCode,
  })

  const [versionsOpen, setVersionsOpen] = useState(false)

  const submitMut = useMutation({
    mutationFn: () => roleMasterApi.submitForApproval(bundle!.master.id),
    onSuccess: () => {
      msg.success('Submitted for approval')
      refetch()
    },
    onError: (e: Error) => msg.error(e.message),
  })

  const approveMut = useMutation({
    mutationFn: () => roleMasterApi.approve(bundle!.master.id),
    onSuccess: () => {
      msg.success('Approved')
      refetch()
    },
    onError: (e: Error) => msg.error(e.message),
  })

  const rejectMut = useMutation({
    mutationFn: (reason: string) =>
      roleMasterApi.reject(bundle!.master.id, reason),
    onSuccess: () => {
      msg.success('Rejected')
      refetch()
    },
    onError: (e: Error) => msg.error(e.message),
  })

  if (isLoading || !bundle) {
    return <PageHeader title="Loading role…" />
  }

  const m = bundle.master

  return (
    <>
      <PageHeader
        title={`${m.designation_name} (${m.role_code})`}
        subtitle={m.role_purpose || 'Detailed responsibilities pending. Add lines below.'}
        extra={
          <Space>
            <Tag color={STATUS_COLORS[m.approval_status]}>{m.approval_status}</Tag>
            <Tag>v{m.current_version_no}</Tag>
            {m.approval_status === 'DRAFT' || m.approval_status === 'DETAILS_PENDING' || m.approval_status === 'REJECTED' ? (
              <Button icon={<SendOutlined />} onClick={() => submitMut.mutate()}>
                Submit for Approval
              </Button>
            ) : null}
            {m.approval_status === 'PENDING_APPROVAL' && (
              <>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => approveMut.mutate()}
                  loading={approveMut.isPending}
                >
                  Approve
                </Button>
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => {
                    let reason = ''
                    modal.confirm({
                      title: 'Reject this role',
                      content: (
                        <Input.TextArea
                          placeholder="Reason for rejection"
                          onChange={(e) => (reason = e.target.value)}
                        />
                      ),
                      onOk: () => {
                        if (!reason.trim()) {
                          msg.error('Reason required')
                          return Promise.reject()
                        }
                        return rejectMut.mutateAsync(reason)
                      },
                    })
                  }}
                >
                  Reject
                </Button>
              </>
            )}
            <Button onClick={() => setVersionsOpen(true)}>Version History</Button>
            <Button onClick={() => navigate('/hr/role-master')}>Back to list</Button>
          </Space>
        }
      />

      {m.approval_status === 'DETAILS_PENDING' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Detailed responsibilities pending"
          description="This role was auto-seeded. Add daily / weekly / monthly responsibility lines below, then submit for approval."
        />
      )}

      <Card size="small" style={{ marginBottom: 12 }}>
        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label="Role Level">{m.role_level || '—'}</Descriptions.Item>
          <Descriptions.Item label="Shift">{m.shift_type || '—'}</Descriptions.Item>
          <Descriptions.Item label="Criticality">{m.criticality}</Descriptions.Item>
          <Descriptions.Item label="Manpower">
            {m.approximate_manpower_min ?? '—'} – {m.approximate_manpower_max ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Statutory">{m.statutory_role_flag ? 'Yes' : 'No'}</Descriptions.Item>
          <Descriptions.Item label="Safety Critical">{m.safety_critical_flag ? 'Yes' : 'No'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs
        defaultActiveKey="responsibilities"
        items={[
          {
            key: 'responsibilities',
            label: <Badge count={bundle.responsibility_lines.length} showZero offset={[8, -4]}>Responsibilities</Badge>,
            children: <SatelliteTab roleId={m.id} kind="lines" bundle={bundle} onChange={refetch} />,
          },
          {
            key: 'kpis',
            label: <Badge count={bundle.kpis.length} showZero offset={[8, -4]}>KPIs</Badge>,
            children: <SatelliteTab roleId={m.id} kind="kpis" bundle={bundle} onChange={refetch} />,
          },
          {
            key: 'authorities',
            label: <Badge count={bundle.authorities.length} showZero offset={[8, -4]}>Authorities</Badge>,
            children: <SatelliteTab roleId={m.id} kind="authorities" bundle={bundle} onChange={refetch} />,
          },
          {
            key: 'records',
            label: <Badge count={bundle.records.length} showZero offset={[8, -4]}>Records</Badge>,
            children: <SatelliteTab roleId={m.id} kind="records" bundle={bundle} onChange={refetch} />,
          },
          {
            key: 'skills',
            label: <Badge count={bundle.skills.length} showZero offset={[8, -4]}>Skills</Badge>,
            children: <SatelliteTab roleId={m.id} kind="skills" bundle={bundle} onChange={refetch} />,
          },
          {
            key: 'trainings',
            label: <Badge count={bundle.trainings.length} showZero offset={[8, -4]}>Training</Badge>,
            children: <SatelliteTab roleId={m.id} kind="trainings" bundle={bundle} onChange={refetch} />,
          },
          {
            key: 'compliance',
            label: <Badge count={bundle.compliance.length} showZero offset={[8, -4]}>Compliance</Badge>,
            children: <SatelliteTab roleId={m.id} kind="compliance" bundle={bundle} onChange={refetch} />,
          },
          {
            key: 'backups',
            label: <Badge count={bundle.backups.length} showZero offset={[8, -4]}>Backup</Badge>,
            children: <SatelliteTab roleId={m.id} kind="backups" bundle={bundle} onChange={refetch} />,
          },
        ]}
      />

      <VersionDrawer
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        roleId={m.id}
      />
    </>
  )
}

// ─── Generic satellite tab ─────────────────────────────────────────────

type SatRow = Record<string, unknown> & { id: string }

function SatelliteTab({
  roleId, kind, bundle, onChange,
}: {
  roleId: string
  kind: Kind
  bundle: RoleBundle
  onChange: () => void
}) {
  const { message: msg, modal } = App.useApp()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<SatRow | null>(null)
  const [form] = Form.useForm()

  const rows = (
    kind === 'lines' ? bundle.responsibility_lines :
    kind === 'kpis' ? bundle.kpis :
    kind === 'records' ? bundle.records :
    kind === 'authorities' ? bundle.authorities :
    kind === 'skills' ? bundle.skills :
    kind === 'trainings' ? bundle.trainings :
    kind === 'compliance' ? bundle.compliance :
    bundle.backups
  ) as SatRow[]

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      roleMasterApi.createSatellite(roleId, kind, values),
    onSuccess: () => {
      msg.success('Added')
      setDrawerOpen(false)
      form.resetFields()
      onChange()
    },
    onError: (e: Error) => msg.error(e.message),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, values }: { id: string; values: Record<string, unknown> }) =>
      roleMasterApi.updateItem(kind, id, values),
    onSuccess: () => {
      msg.success('Updated')
      setDrawerOpen(false)
      setEditing(null)
      form.resetFields()
      onChange()
    },
    onError: (e: Error) => msg.error(e.message),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => roleMasterApi.deleteItem(kind, id, 'Deactivated by user'),
    onSuccess: () => {
      msg.success('Deactivated')
      onChange()
    },
    onError: (e: Error) => msg.error(e.message),
  })

  const onAdd = () => {
    setEditing(null)
    form.resetFields()
    setDrawerOpen(true)
  }
  const onEdit = (r: SatRow) => {
    setEditing(r)
    form.setFieldsValue(r)
    setDrawerOpen(true)
  }

  const columns = getSatelliteColumns(kind, onEdit, (r) =>
    modal.confirm({
      title: 'Deactivate this row?',
      content: 'It will no longer appear in the live list. Version history is preserved.',
      onOk: () => deleteMut.mutateAsync(r.id),
    }),
  )

  return (
    <>
      <Space style={{ marginBottom: 8 }}>
        <Button type="primary" onClick={onAdd}>Add {labelFor(kind)}</Button>
        <Text type="secondary">{rows.length} active rows</Text>
      </Space>
      {rows.length === 0 ? (
        <Empty description={`No ${labelFor(kind)} yet — HR will add later`} />
      ) : (
        <Table
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={rows}
          pagination={false}
        />
      )}

      <Drawer
        title={editing ? `Edit ${labelFor(kind)}` : `Add ${labelFor(kind)}`}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditing(null); form.resetFields() }}
        width={560}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (editing) updateMut.mutate({ id: editing.id, values })
            else createMut.mutate(values)
          }}
        >
          {renderFormFields(kind)}
          <Space>
            <Button type="primary" htmlType="submit" loading={createMut.isPending || updateMut.isPending}>
              {editing ? 'Save changes' : 'Add'}
            </Button>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
          </Space>
        </Form>
      </Drawer>
    </>
  )
}

function labelFor(kind: Kind): string {
  return {
    lines: 'responsibility line',
    kpis: 'KPI',
    records: 'record',
    authorities: 'authority',
    skills: 'skill',
    trainings: 'training',
    compliance: 'compliance area',
    backups: 'backup',
  }[kind]
}

function getSatelliteColumns(
  kind: Kind,
  onEdit: (r: SatRow) => void,
  onDelete: (r: SatRow) => void,
) {
  const action = {
    title: '',
    key: 'action',
    width: 130,
    render: (_: unknown, r: SatRow) => (
      <Space size={4}>
        <Button size="small" onClick={() => onEdit(r)}>Edit</Button>
        <Button size="small" danger onClick={() => onDelete(r)}>Deactivate</Button>
      </Space>
    ),
  }

  if (kind === 'lines') {
    return [
      { title: 'Code', dataIndex: 'responsibility_code', width: 100 },
      { title: 'Type', dataIndex: 'responsibility_type', width: 130 },
      { title: 'Frequency', dataIndex: 'frequency', width: 130 },
      { title: 'Priority', dataIndex: 'priority', width: 80 },
      { title: 'Text', dataIndex: 'responsibility_text' },
      { title: 'Evidence', dataIndex: 'evidence_required', width: 80,
        render: (b: boolean) => (b ? 'Yes' : '') },
      { title: 'v', dataIndex: 'version_no', width: 50 },
      action,
    ]
  }
  if (kind === 'kpis') {
    return [
      { title: 'Code', dataIndex: 'kpi_code', width: 120 },
      { title: 'Name', dataIndex: 'kpi_name' },
      { title: 'Target', dataIndex: 'target_value', width: 100 },
      { title: 'Unit', dataIndex: 'unit', width: 80 },
      { title: 'Frequency', dataIndex: 'frequency', width: 100 },
      { title: 'Auto', dataIndex: 'auto_calculated', width: 60,
        render: (b: boolean) => (b ? 'Yes' : '') },
      action,
    ]
  }
  if (kind === 'authorities') {
    return [
      { title: 'Type', dataIndex: 'authority_type', width: 130 },
      { title: 'Description', dataIndex: 'authority_description' },
      { title: 'Max ₹', dataIndex: 'max_amount', width: 110 },
      { title: 'Module', dataIndex: 'module_name', width: 120 },
      action,
    ]
  }
  if (kind === 'records') {
    return [
      { title: 'Record', dataIndex: 'record_name' },
      { title: 'Type', dataIndex: 'record_type', width: 100 },
      { title: 'Module', dataIndex: 'module_name', width: 120 },
      { title: 'Frequency', dataIndex: 'frequency', width: 100 },
      { title: 'Mandatory', dataIndex: 'mandatory_flag', width: 90,
        render: (b: boolean) => (b ? 'Yes' : '') },
      action,
    ]
  }
  if (kind === 'skills') {
    return [
      { title: 'Skill', dataIndex: 'skill_name' },
      { title: 'Category', dataIndex: 'skill_category', width: 120 },
      { title: 'Min Level', dataIndex: 'minimum_skill_level', width: 90 },
      { title: 'Cert?', dataIndex: 'certification_required', width: 70,
        render: (b: boolean) => (b ? 'Yes' : '') },
      action,
    ]
  }
  if (kind === 'trainings') {
    return [
      { title: 'Module', dataIndex: 'training_module_name' },
      { title: 'Type', dataIndex: 'training_type', width: 130 },
      { title: 'Mandatory', dataIndex: 'mandatory_flag', width: 90,
        render: (b: boolean) => (b ? 'Yes' : '') },
      { title: 'Pre-deploy', dataIndex: 'before_deployment_flag', width: 100,
        render: (b: boolean) => (b ? 'Yes' : '') },
      action,
    ]
  }
  if (kind === 'compliance') {
    return [
      { title: 'Area', dataIndex: 'compliance_area' },
      { title: 'Legal Reference', dataIndex: 'legal_reference' },
      { title: 'Mandatory', dataIndex: 'mandatory_flag', width: 90,
        render: (b: boolean) => (b ? 'Yes' : '') },
      { title: 'Renewal', dataIndex: 'renewal_required', width: 80,
        render: (b: boolean) => (b ? 'Yes' : '') },
      action,
    ]
  }
  // backups
  return [
    { title: 'Backup role id', dataIndex: 'backup_role_id' },
    { title: 'Type', dataIndex: 'backup_type', width: 130 },
    { title: 'Priority', dataIndex: 'priority_order', width: 90 },
    action,
  ]
}

function renderFormFields(kind: Kind) {
  if (kind === 'lines') {
    return (
      <>
        <Form.Item name="responsibility_code" label="Responsibility code" rules={[{ required: true }]}>
          <Input placeholder="e.g. RFT_D_01" />
        </Form.Item>
        <Form.Item name="responsibility_type" label="Type" rules={[{ required: true }]}>
          <Select options={RESPONSIBILITY_TYPES.map((v) => ({ value: v, label: v }))} />
        </Form.Item>
        <Form.Item name="frequency" label="Frequency" rules={[{ required: true }]}>
          <Select options={FREQUENCIES.map((v) => ({ value: v, label: v }))} />
        </Form.Item>
        <Form.Item name="responsibility_text" label="Responsibility text" rules={[{ required: true }]}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="priority" label="Priority">
          <Select allowClear options={PRIORITIES.map((v) => ({ value: v, label: v }))} />
        </Form.Item>
        <Form.Item name="evidence_required" label="Evidence required" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="linked_module" label="Linked module">
          <Input placeholder="e.g. PRODUCTION, MAINTENANCE" />
        </Form.Item>
        <Form.Item name="sequence_no" label="Sequence">
          <InputNumber min={0} />
        </Form.Item>
      </>
    )
  }
  if (kind === 'kpis') {
    return (
      <>
        <Form.Item name="kpi_code" label="KPI code" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="kpi_name" label="KPI name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="target_value" label="Target value">
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="unit" label="Unit">
          <Input placeholder="%, kg, count, etc." />
        </Form.Item>
        <Form.Item name="frequency" label="Frequency">
          <Select options={FREQUENCIES.map((v) => ({ value: v, label: v }))} allowClear />
        </Form.Item>
        <Form.Item name="data_source_module" label="Data source module">
          <Input />
        </Form.Item>
        <Form.Item name="auto_calculated" label="Auto-calculated" valuePropName="checked">
          <Switch />
        </Form.Item>
      </>
    )
  }
  if (kind === 'authorities') {
    return (
      <>
        <Form.Item name="authority_type" label="Type" rules={[{ required: true }]}>
          <Select options={['APPROVE','REJECT','ESCALATE','STOP_WORK','STOP_MACHINE','STOP_DISPATCH','HOLD_MATERIAL','RELEASE_MATERIAL','OVERRIDE','VIEW_ONLY'].map((v) => ({ value: v, label: v }))} />
        </Form.Item>
        <Form.Item name="authority_description" label="Description">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="max_amount" label="Max amount (₹)">
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
        <Form.Item name="module_name" label="Module">
          <Input />
        </Form.Item>
        <Form.Item name="can_stop_machine" label="Can stop machine" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="can_override" label="Can override" valuePropName="checked">
          <Switch />
        </Form.Item>
      </>
    )
  }
  if (kind === 'records') {
    return (
      <>
        <Form.Item name="record_name" label="Record name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="record_type" label="Type">
          <Select options={['REGISTER','FORM','LOG','REPORT','CERTIFICATE'].map((v) => ({ value: v, label: v }))} allowClear />
        </Form.Item>
        <Form.Item name="module_name" label="Module">
          <Input />
        </Form.Item>
        <Form.Item name="frequency" label="Frequency">
          <Select options={FREQUENCIES.map((v) => ({ value: v, label: v }))} allowClear />
        </Form.Item>
        <Form.Item name="mandatory_flag" label="Mandatory" valuePropName="checked" initialValue>
          <Switch />
        </Form.Item>
        <Form.Item name="retention_period_months" label="Retention (months)">
          <InputNumber min={0} />
        </Form.Item>
      </>
    )
  }
  if (kind === 'skills') {
    return (
      <>
        <Form.Item name="skill_name" label="Skill name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="skill_category" label="Category">
          <Select options={['TECHNICAL','BEHAVIOURAL','STATUTORY','DIGITAL'].map((v) => ({ value: v, label: v }))} allowClear />
        </Form.Item>
        <Form.Item name="minimum_skill_level" label="Minimum skill level (1-5)" rules={[{ required: true }]}>
          <InputNumber min={1} max={5} />
        </Form.Item>
        <Form.Item name="certification_required" label="Certification required" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="refresher_required" label="Refresher required" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="refresher_frequency_days" label="Refresher (days)">
          <InputNumber min={1} />
        </Form.Item>
      </>
    )
  }
  if (kind === 'trainings') {
    return (
      <>
        <Form.Item name="training_module_name" label="Training module name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="training_module_code" label="Module code">
          <Input />
        </Form.Item>
        <Form.Item name="training_type" label="Type" rules={[{ required: true }]}>
          <Select options={['INDUCTION','SAFETY','MACHINE_OPERATION','QUALITY','MAINTENANCE','COMPLIANCE','ERP','SUPERVISORY','FIRE','FIRST_AID'].map((v) => ({ value: v, label: v }))} />
        </Form.Item>
        <Form.Item name="mandatory_flag" label="Mandatory" valuePropName="checked" initialValue>
          <Switch />
        </Form.Item>
        <Form.Item name="before_deployment_flag" label="Required before deployment" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="refresher_frequency_days" label="Refresher (days)">
          <InputNumber min={1} />
        </Form.Item>
      </>
    )
  }
  if (kind === 'compliance') {
    return (
      <>
        <Form.Item name="compliance_area" label="Compliance area" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="legal_reference" label="Legal reference">
          <Input />
        </Form.Item>
        <Form.Item name="mandatory_flag" label="Mandatory" valuePropName="checked" initialValue>
          <Switch />
        </Form.Item>
        <Form.Item name="licence_required" label="Licence required" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="renewal_required" label="Renewal required" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="renewal_frequency_days" label="Renewal (days)">
          <InputNumber min={1} />
        </Form.Item>
      </>
    )
  }
  // backups
  return (
    <>
      <Form.Item name="backup_role_id" label="Backup role id (UUID)" rules={[{ required: true }]}>
        <Input placeholder="paste role id from role list" />
      </Form.Item>
      <Form.Item name="backup_type" label="Backup type" rules={[{ required: true }]}>
        <Select options={['SAME_SHIFT','NEXT_SHIFT','ON_CALL','TEMPORARY','EMERGENCY'].map((v) => ({ value: v, label: v }))} />
      </Form.Item>
      <Form.Item name="priority_order" label="Priority order" rules={[{ required: true }]}>
        <InputNumber min={1} />
      </Form.Item>
    </>
  )
}

// ─── Version history drawer ────────────────────────────────────────────

function VersionDrawer({
  open, onClose, roleId,
}: { open: boolean; onClose: () => void; roleId: string }) {
  const { data: versions = [], isLoading } = useQuery({
    queryKey: ['role-versions', roleId, open],
    queryFn: () => roleMasterApi.listVersions(undefined, roleId),
    enabled: open,
  })

  return (
    <Drawer
      title="Version History"
      open={open}
      onClose={onClose}
      width={680}
    >
      {isLoading ? (
        <Text>Loading…</Text>
      ) : versions.length === 0 ? (
        <Empty />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          {versions.map((v: VersionRow) => (
            <Card key={v.id} size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                  <Tag color="blue">{v.entity_type}</Tag>
                  <Tag>v{v.version_no}</Tag>
                  <Text type="secondary">{new Date(v.changed_at).toLocaleString()}</Text>
                  <Tag color={v.approval_status === 'APPROVED' ? 'green' : 'gold'}>{v.approval_status}</Tag>
                </Space>
                {v.change_reason && <Paragraph>{v.change_reason}</Paragraph>}
                <Text code style={{ fontSize: 11 }}>
                  {JSON.stringify(v.new_value_json, null, 2).substring(0, 400)}
                  {JSON.stringify(v.new_value_json).length > 400 ? '…' : ''}
                </Text>
              </Space>
            </Card>
          ))}
        </Space>
      )}
    </Drawer>
  )
}
